/**
 * ComponentSplit — Split family renderer.
 *
 * Images occupy one half of the component, content (titles, text,
 * actions) occupies the other half. Variants:
 *   - "split_left": images on left, content on right (contained)
 *   - "split_right": images on right, content on left (contained)
 *   - "split_left_full": image fills entire left half edge-to-edge
 *   - "split_right_full": image fills entire right half edge-to-edge
 *   - "split_left_carousel": full-bleed images auto-rotate on the left (crossfade)
 *   - "split_right_carousel": full-bleed images auto-rotate on the right (crossfade)
 *
 * Carousels render edge-to-edge cover crops like the "_full" variants, with the
 * pagination dots pinned inside the media at the bottom-center.
 *
 * On mobile (small/extra_small), stacks to single column with images
 * on top.
 *
 * Edit mode: add buttons appear after media and each content bucket.
 * Empty buckets show placeholder affordances.
 */
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import {
  bucketElements,
  BucketRow,
  RenderItem,
  BucketAddButton,
  EmptyBucketPlaceholder,
  elementHasRenderableMedia,
  SortableItemWrapper,
} from "./componentModeUtils";
import { EditContext } from "../../../editor/context/EditContext";
import { DragHandleProvider } from "../../../editor/context/DragHandleContext";
import { makeSortableId } from "../../../editor/utils/reorderCache";

const FULL_BLEED_MAX_HEIGHT = "600px";
// `!items-center`/`!justify-center` neutralise the per-image `position` flex
// alignment from `Image.js` so cover-cropped media stays centered in its tile
// instead of being parked in its `position` corner (e.g. bottom-right).
// `[&_.image-size-box]:!p-0` zeroes the image's OWN padding inside a cover tile —
// the tile is the layout-owned frame, so per-image padding would inset the cover
// <img> inside an `overflow-hidden` wrapper and crop its rounded corners square.
const COVER_FILL = "[&_div]:!h-full [&_div]:!w-full [&_div]:!max-w-none [&_div]:!items-center [&_div]:!justify-center [&_.image-size-box]:!p-0 [&_.progressive-image-wrapper]:!h-full [&_.progressive-image-wrapper]:!w-full [&_.progressive-image-wrapper]:!max-w-none [&_img]:!w-full [&_img]:!h-full [&_img]:!object-cover [&_img]:!object-center [&_img]:!max-h-none [&_img]:!max-w-none [&_img]:!box-border [&_video]:!w-full [&_video]:!h-full [&_video]:!object-cover [&_video]:!object-center";
const FILL_WIDTH = "[&_div]:!w-full [&_div]:!max-w-none [&_.progressive-image-wrapper]:!w-full [&_.progressive-image-wrapper]:!max-w-none [&_img]:!w-full [&_img]:!h-auto [&_img]:!max-w-none [&_img]:!max-h-none [&_video]:!w-full [&_video]:!h-auto [&_video]:!max-w-none";
const CONTAINED_TILE = `rounded-lg ${FILL_WIDTH}`;
// Invisible "sizer" that lets a full-width image dictate its cell's natural
// height (so an `absolute inset-0` cover layer or a stack of crossfading slides
// has something to size against when the sibling content column is short).
const FULL_BLEED_SIZER = "invisible [&_div]:!w-full [&_div]:!max-w-none [&_.progressive-image-wrapper]:!w-full [&_.progressive-image-wrapper]:!max-w-none [&_img]:!w-full [&_img]:!max-w-none";

function isFullBleed(modeId) {
  return modeId === "split_left_full" || modeId === "split_right_full";
}

function isMediaRight(modeId) {
  return (
    modeId === "split_right" ||
    modeId === "split_right_full" ||
    modeId === "split_right_carousel"
  );
}

function isCarousel(modeId) {
  return modeId === "split_left_carousel" || modeId === "split_right_carousel";
}

// ---------------------------------------------------------------------------
// Carousel hook — auto-advancing crossfade
// ---------------------------------------------------------------------------

// Slow, gentle pacing with a TRUE crossfade — slides stay mounted and the
// outgoing layer fades 1→0 while the incoming fades 0→1 simultaneously (see
// renderCarouselMedia), so there is no fade-to-blank flash between slides.
const CAROUSEL_INTERVAL_MS = 6500;
const CAROUSEL_FADE_MS = 1200;

function useSplitCarousel(count, { paused = false } = {}) {
  const [activeIndex, setActiveIndex] = useState(0);
  const timerRef = useRef(null);

  const prefersReducedMotion = useMemo(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches || false;
  }, []);

  useEffect(() => {
    setActiveIndex(0);
  }, [count]);

  useEffect(() => {
    if (paused || prefersReducedMotion || count <= 1) return;

    let cancelled = false;

    const tick = () => {
      if (cancelled) return;
      setActiveIndex((prev) => (prev + 1) % count);
    };

    const start = () => {
      if (timerRef.current) return;
      timerRef.current = setInterval(tick, CAROUSEL_INTERVAL_MS);
    };
    const stop = () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };

    const handleVisibility = () => {
      if (document.hidden) stop();
      else start();
    };

    if (!document.hidden) start();
    document.addEventListener("visibilitychange", handleVisibility);
    return () => {
      cancelled = true;
      stop();
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [count, paused, prefersReducedMotion]);

  return { activeIndex };
}

// ---------------------------------------------------------------------------
// Renderer
// ---------------------------------------------------------------------------

const ComponentSplit = ({ component, elements, modeDefinition, breakpoint }) => {
  const modeId = component?.component_mode;
  const mediaOnRight = isMediaRight(modeId);
  const fullBleed = isFullBleed(modeId);
  const carousel = isCarousel(modeId);
  const { bucketed } = bucketElements(elements, modeDefinition);
  const editContext = useContext(EditContext);
  const isEditMode = !!editContext;

  const images = bucketed.image || [];
  const titles = bucketed.title || [];
  const texts = bucketed.text || [];
  const buttonCollections = bucketed.button_collection || [];
  const buttons = bucketed.button || [];
  const files = bucketed.file || [];

  const compId = component?.id;
  const isMobile = breakpoint === "small" || breakpoint === "extra_small";
  const totalElements = elements?.length || 0;

  const { activeIndex } = useSplitCarousel(images.length, {
    paused: !carousel,
  });

  const imagesHaveMedia = images.length > 0 && images.some(elementHasRenderableMedia);

  // ---- Per-image drag-to-reorder for bare-RenderItem cover tiles -----------
  // Full-bleed images render through a bare `RenderItem` inside an
  // `overflow-hidden` cover cell. Without an own sortable node each image's
  // EditableElement inherits the COMPONENT's drag context, so its drag "dots"
  // pill renders clipped inside the cover tile (and would drag the whole
  // component). Making the cover tile itself the sortable node (drag-anywhere →
  // no pill) lets a drag reorder the image and lets the selection chrome portal
  // above the tile. Mirrors ComponentGallery / ComponentMosaic.
  const imageSortData = (img, index) => ({
    parentType: "component",
    parentId: compId,
    kind: "item",
    itemType: img.type,
    index,
    autoLayout: true,
    autoBucket: null,
  });

  const imageSortIds = images.map((img) => makeSortableId(img));
  const withImageSortContext = (node) => {
    if (!isEditMode || imageSortIds.length === 0) return node;
    return (
      <SortableContext items={imageSortIds} strategy={verticalListSortingStrategy}>
        {node}
      </SortableContext>
    );
  };

  // Auto-advancing carousel slides are not individually reorderable, and empty
  // placeholders are not draggable, so we neutralise the inherited component
  // drag context for them — no clipped pill, image stays selectable. Mirrors
  // ComponentGallery's carousel handling.
  const SuppressDrag = ({ children }) =>
    isEditMode ? (
      <DragHandleProvider value={{ dragListeners: null, dragAnywhereEnabled: false }}>
        {children}
      </DragHandleProvider>
    ) : (
      children
    );

  // Pagination dots, pinned INSIDE the media at the bottom-center (over the
  // full-bleed cover image) so they read as part of the slide rather than a
  // detached control sitting below it.
  const renderCarouselDots = () =>
    images.length > 1 ? (
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-1.5 z-10">
        {images.map((img, idx) => (
          <span
            key={img.id}
            className={`w-2 h-2 rounded-full transition-all duration-300 ${
              idx === activeIndex
                ? "bg-white shadow-md scale-125"
                : "bg-white/50"
            }`}
          />
        ))}
      </div>
    ) : null;

  // Full-bleed crossfade: slides stack edge-to-edge and cover-crop their cell
  // (matching the split_*_full variants) instead of rendering contained. On
  // desktop an invisible sizer gives the cell its natural height; on mobile the
  // cell is locked to a 16/9 frame, mirroring the full-bleed mobile layout.
  const renderCarouselMedia = ({ mobile = false } = {}) => {
    if (images.length === 0) return null;

    if (!imagesHaveMedia) {
      return (
        <div className="w-full">
          <SuppressDrag>
            <RenderItem element={images[activeIndex]} />
          </SuppressDrag>
        </div>
      );
    }

    return (
      <div
        className={`relative overflow-hidden ${mobile ? "w-full" : "w-full h-full"}`}
        style={mobile ? { aspectRatio: "16 / 9" } : { maxHeight: FULL_BLEED_MAX_HEIGHT }}
      >
        {!mobile && (
          <div className={FULL_BLEED_SIZER} aria-hidden="true">
            <RenderItem element={images[0]} />
          </div>
        )}
        {images.map((img, idx) => (
          <div
            key={img.id}
            className={`absolute inset-0 transition-opacity ease-in-out ${COVER_FILL}`}
            style={{
              transitionDuration: `${CAROUSEL_FADE_MS}ms`,
              opacity: idx === activeIndex ? 1 : 0,
              pointerEvents: idx === activeIndex ? "auto" : "none",
            }}
          >
            <SuppressDrag>
              <RenderItem element={img} />
            </SuppressDrag>
          </div>
        ))}
        {renderCarouselDots()}
      </div>
    );
  };

  // A single full-bleed image tile. Real media cover-crops inside an
  // `overflow-hidden` cell that, in edit mode, IS the per-image drag-to-reorder
  // sortable node (drag-anywhere → no clipped pill). Placeholders render
  // naturally (no cover clip) and are non-draggable.
  const renderFullBleedTile = (img, index, { mobile }) => {
    if (!elementHasRenderableMedia(img)) {
      return (
        <SuppressDrag key={img.id}>
          <div
            className={
              mobile
                ? "relative w-full flex items-center justify-center p-2"
                : "relative flex-1 flex items-center justify-center p-2"
            }
            style={mobile ? undefined : { maxHeight: FULL_BLEED_MAX_HEIGHT }}
          >
            <RenderItem element={img} />
          </div>
        </SuppressDrag>
      );
    }

    const cellClassName = mobile
      ? "relative overflow-hidden w-full"
      : "relative overflow-hidden flex-1";
    const cellStyle = mobile
      ? { aspectRatio: "16 / 9" }
      : { maxHeight: FULL_BLEED_MAX_HEIGHT };
    const inner = (
      <>
        {!mobile && (
          <div className={FULL_BLEED_SIZER} aria-hidden="true">
            <RenderItem element={img} />
          </div>
        )}
        <div className={`absolute inset-0 ${COVER_FILL}`}>
          <RenderItem element={img} />
        </div>
      </>
    );

    if (!isEditMode) {
      return (
        <div key={img.id} className={cellClassName} style={cellStyle}>
          {inner}
        </div>
      );
    }
    return (
      <SortableItemWrapper
        key={img.id}
        id={makeSortableId(img)}
        data={imageSortData(img, index)}
        className={cellClassName}
        style={cellStyle}
      >
        {inner}
      </SortableItemWrapper>
    );
  };

  const MediaColumn = (
    <div className={isMobile ? "w-full" : "w-1/2 flex-shrink-0"}>
      {images.length > 0 ? (
        <>
          {carousel ? (
            renderCarouselMedia({ mobile: isMobile })
          ) : fullBleed && !isMobile ? (
            withImageSortContext(
              <div className="flex flex-col w-full h-full">
                {images.map((img, index) => renderFullBleedTile(img, index, { mobile: false }))}
              </div>
            )
          ) : fullBleed && isMobile ? (
            withImageSortContext(
              <div className="flex flex-col w-full gap-4">
                {images.map((img, index) => renderFullBleedTile(img, index, { mobile: true }))}
              </div>
            )
          ) : (
            <BucketRow
              elements={images}
              compId={compId}
              className="w-full flex flex-col gap-2"
              cellClassName={CONTAINED_TILE}
            />
          )}
          {isEditMode && !fullBleed && !carousel && (
            <BucketAddButton compId={compId} bucket="image" order={totalElements} className="-mt-4 z-10" />
          )}
        </>
      ) : isEditMode ? (
        <EmptyBucketPlaceholder compId={compId} bucket="image" order={totalElements} />
      ) : null}
    </div>
  );

  const contentItems = [...titles, ...texts, ...buttonCollections, ...buttons]
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  const ContentColumn = (
    <div className={isMobile ? "w-full" : "w-1/2 flex flex-col justify-center"}>
      {contentItems.length > 0 && (
        <BucketRow elements={contentItems} compId={compId} className="w-full" />
      )}

      {isEditMode && (
        <>
          {titles.length === 0 && (
            <EmptyBucketPlaceholder compId={compId} bucket="title" order={totalElements} />
          )}
          {texts.length === 0 && (
            <EmptyBucketPlaceholder compId={compId} bucket="text" order={totalElements} />
          )}
          {contentItems.length > 0 && (
            <BucketAddButton compId={compId} bucket="text" order={totalElements} />
          )}
        </>
      )}
    </div>
  );

  const FilesRow = files.length > 0 ? (
    <BucketRow elements={files} compId={compId} className="w-full" />
  ) : null;

  if (isMobile) {
    return (
      <div className="w-full flex flex-col group/split">
        {MediaColumn}
        {ContentColumn}
        {FilesRow}
      </div>
    );
  }

  return (
    <div className="w-full group/split">
      <div className={`flex ${mediaOnRight ? "flex-row-reverse" : "flex-row"}`}>
        {MediaColumn}
        {ContentColumn}
      </div>
      {FilesRow}
    </div>
  );
};

export default React.memo(ComponentSplit);
